import React, { useState, useEffect, useRef } from "react";
import { X, ImagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import StarRating from "@/components/ui/StarRating";
import ReviewStrengthMeter from "@/components/ReviewStrengthMeter";
import { RatingFactors, ratingFactorLabels, CreateReviewRequest, Review } from "@/types/review";
import { reviewService } from "@/api/reviewService";

interface ReviewFormProps {
  storeId: string;
  categorySlug: string;
  onSubmit: (reviewData: CreateReviewRequest, images: File[]) => Promise<void>;
  onCancel: () => void;
  existingReview?: Review;
  isLoading?: boolean;
}

const MAX_IMAGES = 5;

const ReviewForm: React.FC<ReviewFormProps> = ({
  storeId,
  categorySlug,
  onSubmit,
  onCancel,
  existingReview,
  isLoading = false,
}) => {
  const [overallRating, setOverallRating] = useState(existingReview?.overallRating || 0);
  const [text, setText] = useState(existingReview?.text || "");
  const [ratingFactors, setRatingFactors] = useState<RatingFactors>(
    existingReview?.ratingFactors || {}
  );
  const [isAnonymous, setIsAnonymous] = useState(existingReview?.isAnonymous || false);
  const [availableFactors, setAvailableFactors] = useState<string[]>([]);
  const [loadingFactors, setLoadingFactors] = useState(false);

  // Image state
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isEditing = !!existingReview;

  useEffect(() => {
    const loadRatingFactors = async () => {
      setLoadingFactors(true);
      try {
        // `getRatingFactorKeys`, not `getRatingFactors`: the latter's declared `{ ok, data }`
        // envelope never exists at runtime (request() already unwrapped it), so the old
        // `response.ok && response.data` guard was always false and this section has never
        // rendered. That is the real reason no native review has a single rating factor.
        const factors = await reviewService.getRatingFactorKeys(categorySlug);
        if (factors.length > 0) {
          setAvailableFactors(factors);
        }
      } catch {
        setAvailableFactors(["quality", "service", "price", "experience"]);
      } finally {
        setLoadingFactors(false);
      }
    };

    if (categorySlug) {
      loadRatingFactors();
    }
  }, [categorySlug]);

  const handleFactorRatingChange = (factor: string, rating: number) => {
    setRatingFactors((prev) => ({ ...prev, [factor]: rating }));
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_IMAGES - selectedImages.length;
    const toAdd = files.slice(0, remaining).filter((f) => f.type.startsWith("image/"));

    const newPreviews = toAdd.map((f) => URL.createObjectURL(f));
    setSelectedImages((prev) => [...prev, ...toAdd]);
    setImagePreviews((prev) => [...prev, ...newPreviews]);

    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = (idx: number) => {
    URL.revokeObjectURL(imagePreviews[idx]);
    setSelectedImages((prev) => prev.filter((_, i) => i !== idx));
    setImagePreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (overallRating === 0) {
      alert("يرجى تحديد التقييم العام");
      return;
    }

    const reviewData: CreateReviewRequest = {
      overallRating,
      ratingFactors,
      isAnonymous,
    };

    if (text.trim()) {
      reviewData.text = text.trim();
    }

    try {
      await onSubmit(reviewData, selectedImages);
    } catch {
      // handled by parent
    }
  };

  const canSubmit = overallRating > 0 && !isLoading && !uploadingImages;

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-arabic">
          {isEditing ? "تعديل التقييم" : "إضافة تقييم"}
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X className="w-4 h-4" />
        </Button>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Overall Rating */}
        <div className="space-y-2">
          <Label className="text-base font-medium font-arabic">التقييم العام *</Label>
          <div className="flex items-center gap-4">
            <StarRating rating={overallRating} onRatingChange={setOverallRating} size="lg" />
            {overallRating > 0 && (
              <span className="text-lg font-medium font-arabic">{overallRating} من 5</span>
            )}
          </div>
        </div>

        <Separator />

        {/* Rating Factors */}
        {!loadingFactors && availableFactors.length > 0 && (
          <div className="space-y-4">
            <Label className="text-base font-medium font-arabic">
              تقييم تفصيلي (اختياري)
            </Label>
            <div className="grid gap-4">
              {availableFactors.map((factor) => (
                <div key={factor} className="flex items-center justify-between">
                  <span className="text-sm font-medium font-arabic">
                    {ratingFactorLabels[factor] || factor}
                  </span>
                  <StarRating
                    rating={ratingFactors[factor as keyof RatingFactors] || 0}
                    onRatingChange={(rating) => handleFactorRatingChange(factor, rating)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <Separator />

        {/* Review Text */}
        <div className="space-y-2">
          <Label htmlFor="reviewText" className="text-base font-medium font-arabic">
            تعليق (اختياري)
          </Label>
          <Textarea
            id="reviewText"
            placeholder="اكتب تجربتك مع هذا المتجر..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="min-h-[100px] font-arabic"
            maxLength={1000}
          />
          <p className="text-sm text-gray-500 font-arabic">{text.length}/1000 حرف</p>
        </div>

        {/* Image upload */}
        <div className="space-y-3">
          <Label className="text-base font-medium font-arabic">
            صور (اختياري، بحد أقصى {MAX_IMAGES})
          </Label>
          <div className="flex flex-wrap gap-2">
            {imagePreviews.map((src, idx) => (
              <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border">
                <img src={src} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(idx)}
                  className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 text-white hover:bg-black/80"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            {selectedImages.length < MAX_IMAGES && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-gray-400 transition-colors"
              >
                <ImagePlus className="w-6 h-6" />
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleImageSelect}
          />
        </div>

        {/* Anonymous */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="anonymous"
            checked={isAnonymous}
            onCheckedChange={(checked) => setIsAnonymous(checked as boolean)}
          />
          <Label htmlFor="anonymous" className="text-sm font-arabic mr-2">
            نشر التقييم بشكل مجهول
          </Label>
        </div>

        {/* Strength meter. Rendered on the EDIT path too: `awardForReview` re-runs on edit and
            pays only the newly earned components, so the preview is honest there as well, and
            editing is exactly when someone adds the photo they skipped. */}
        <ReviewStrengthMeter
          text={text}
          imagesCount={(existingReview?.images?.length ?? 0) + selectedImages.length}
          factorsCount={
            Object.values(ratingFactors).filter((v) => typeof v === "number" && v > 0).length
          }
        />

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex-1 font-arabic"
          >
            {isLoading || uploadingImages
              ? "جاري الحفظ..."
              : isEditing
              ? "حفظ التعديلات"
              : "نشر التقييم"}
          </Button>
          <Button variant="outline" onClick={onCancel} className="font-arabic">
            إلغاء
          </Button>
        </div>

      </CardContent>
    </Card>
  );
};

export { ReviewForm as default };
export type { ReviewFormProps };
